import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appelApi } from './api';

export interface EtatGoogleCalendar {
  connecte: boolean;
  email?: string;
}

export interface EvenementGoogle {
  id: string;
  titre: string;
  description: string | null;
  lieu: string | null;
  debut: string;
  fin: string;
  journeeEntiere: boolean;
}

export interface EntreeEvenementGoogle {
  titre: string;
  description?: string;
  lieu?: string;
  debut: string;
  fin: string;
  journeeEntiere: boolean;
}

const CLE_ETAT = ['calendrier-google', 'etat'] as const;

/**
 * Période de rafraîchissement automatique des événements Google.
 *
 * Le calendrier doit rester à jour sans geste de l'utilisateur : un événement
 * créé depuis l'agenda Google apparaît ici tout seul. Cinq minutes est un
 * compromis — assez court pour que l'écran ne mente pas longtemps, assez long
 * pour ne pas consommer le quota Google avec un onglet laissé ouvert la
 * journée. Le sondage s'arrête quand l'onglet passe en arrière-plan
 * (`refetchIntervalInBackground` reste à `false`) et reprend au retour.
 */
const PERIODE_SYNCHRO_MS = 5 * 60_000;

/** En deçà, deux allers-retours de mois ne relancent pas d'appel. */
const FRAICHEUR_MS = 60_000;

export function useEtatGoogleCalendar() {
  return useQuery({
    queryKey: CLE_ETAT,
    queryFn: () => appelApi<EtatGoogleCalendar>('/calendrier-google/etat'),
    // Une connexion révoquée depuis le compte Google doit se voir sans avoir à
    // recharger la page : l'écran proposera de reconnecter.
    refetchOnWindowFocus: true,
    refetchInterval: PERIODE_SYNCHRO_MS,
  });
}

/** Redirige tout de suite vers Google : ce n'est pas un simple GET de données. */
export function useConnexionGoogleCalendar() {
  return useMutation({
    mutationFn: async () => {
      const { url } = await appelApi<{ url: string }>('/calendrier-google/connexion');
      window.location.href = url;
    },
  });
}

export function useDeconnexionGoogleCalendar() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => appelApi<void>('/calendrier-google/deconnexion', { methode: 'POST' }),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: CLE_ETAT });
      client.invalidateQueries({ queryKey: ['calendrier-google', 'evenements'] });
    },
  });
}

export function useEvenementsGoogleCalendar(depuis: string, jusqua: string, actif: boolean) {
  return useQuery({
    queryKey: ['calendrier-google', 'evenements', depuis, jusqua],
    queryFn: () =>
      appelApi<{ evenements: EvenementGoogle[] }>(
        `/calendrier-google/evenements?depuis=${encodeURIComponent(depuis)}&jusqua=${encodeURIComponent(jusqua)}`,
      ).then((reponse) => reponse.evenements),
    enabled: actif,
    // Synchronisation automatique : ni le bouton ni un rechargement ne sont
    // nécessaires pour voir les événements ajoutés côté Google.
    refetchInterval: actif ? PERIODE_SYNCHRO_MS : false,
    refetchOnWindowFocus: actif,
    staleTime: FRAICHEUR_MS,
  });
}

function invaliderEvenements(client: ReturnType<typeof useQueryClient>): void {
  client.invalidateQueries({ queryKey: ['calendrier-google', 'evenements'] });
}

export function useCreerEvenementGoogle() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (entree: EntreeEvenementGoogle) =>
      appelApi<EvenementGoogle>('/calendrier-google/evenements', { methode: 'POST', corps: entree }),
    onSuccess: () => invaliderEvenements(client),
  });
}

export function useModifierEvenementGoogle() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, entree }: { id: string; entree: EntreeEvenementGoogle }) =>
      appelApi<EvenementGoogle>(`/calendrier-google/evenements/${id}`, { methode: 'PATCH', corps: entree }),
    onSuccess: () => invaliderEvenements(client),
  });
}

export function useSupprimerEvenementGoogle() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      appelApi<void>(`/calendrier-google/evenements/${id}`, { methode: 'DELETE' }),
    onSuccess: () => invaliderEvenements(client),
  });
}
